import { useState, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { useLanguageSync } from '../../hooks/useLanguageSync';
import { getServerUrl } from '../../lib/getServerUrl';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface LotteryParticipant {
  audienceId: string;
  nickname: string;
}

export type ProjectionMode =
  | { type: 'standby' }
  | { type: 'qrcodes'; sessionId: string }
  | { type: 'sheet-vote'; sessionId: string; activeTeam?: string }
  | { type: 'sheet-vote-results'; sessionId: string; activeTeam?: string }
  | { type: 'sheet-vote-winners'; sessionId: string; activeTeam?: string }
  | { type: 'sheet-vote-all-winners'; sessionId: string }
  | { type: 'streaming'; streamUrl: string }
  | { type: 'character'; characterId: string }
  | { type: 'backdrop'; url: string; effect?: string }
  | { type: 'video'; url: string }
  | { type: 'message'; text: string }
  | { type: 'poll-prepare'; sessionId: string; targetTeam?: TeamColor }
  | { type: 'poll-voting'; sessionId: string; pollId: string; targetTeam?: TeamColor }
  | { type: 'poll-results'; sessionId: string; pollId: string }
  | { type: 'bond-vote'; sessionId: string; voteId: string; activeTeam?: TeamColor }
  | { type: 'bond-vote-results'; sessionId: string; voteId: string; activeTeam?: TeamColor }
  | { type: 'bond-vote-winner'; sessionId: string; voteId: string; fromTeam: TeamColor; heroName: string; heroImageUrl?: string; bondText: string }
  | { type: 'bond-vote-all-winners'; sessionId: string }
  | { type: 'lottery'; sessionId: string; lotteryId: string; phase: 'voting' | 'winner'; participants: LotteryParticipant[] }
  | { type: 'lottery-winner'; sessionId: string; lotteryId: string; winnerId: string | null; winnerNickname: string | null; participants: LotteryParticipant[] }
  | { type: 'challenge-active'; sessionId: string; challengeId: string; variant: 'mole' | 'balance' | 'pixelart'; config: any; targetGrid?: number[][]; collectiveGrid?: number[][]; averageAccuracy?: number }
  | { type: 'challenge-result'; sessionId: string; challengeId: string; variant: 'mole' | 'balance' | 'pixelart'; result: 'success' | 'failure'; finalScore: number; targetScore: number; whiteClicks?: number; blackClicks?: number; averageAccuracy?: number; collectiveAccuracy?: number; collectiveGrid?: number[][]; targetGrid?: number[][] }
  | { type: 'consiglio'; sessionId: string }
  | { type: 'consiglio-entry'; sessionId: string; entryId: string; text: string; nickname: string }
  | { type: 'battle'; sessionId: string; battleId: string; battleState: any }
  | { type: 'hero-hp'; sessionId: string; heroName: string; teamColor: string; currentHP: number; maxHP: number }
  | { type: 'bond-auto-assign'; sessionId: string; allBonds: Array<{ team: string; text: string }>; winners: Array<{ team: string; text: string }> };

/**
 * Hook per gestire lo stato display e connessione Socket.IO
 */
export function useDisplayState(sessionId: string | null) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [mode, setMode] = useState<ProjectionMode>({ type: 'standby' });
  const [isConnected, setIsConnected] = useState(false);

  // Sync language changes from server
  useLanguageSync(socket, isConnected);

  useEffect(() => {
    if (!sessionId) return;

    // Connect to Socket.IO display namespace (usa VITE_API_URL per Railway, altrimenti hostname:3000 per LAN)
    const serverUrl = getServerUrl('display');
    const newSocket = io(serverUrl, {
      transports: ['websocket', 'polling'],
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('[DISPLAY] Connected to Socket.IO');
      setIsConnected(true);

      // Join session room
      newSocket.emit('display:join-session', {
        sessionId,
      });
    });

    newSocket.on('display:joined', (data: any) => {
      console.log('[DISPLAY] Joined session:', data.sessionId);
      if (data.currentProjectionMode) {
        setMode(data.currentProjectionMode);
      }
    });

    newSocket.on('disconnect', () => {
      console.log('[DISPLAY] Disconnected from Socket.IO');
      setIsConnected(false);
    });

    newSocket.on('joined', (data: any) => {
      console.log('[DISPLAY] Joined room:', data.room);
    });

    // Listen for projection mode changes from admin
    newSocket.on('display:projection-update', (data: { mode: ProjectionMode }) => {
      console.log('[DISPLAY] Projection mode update:', data.mode);
      setMode(data.mode);
    });

    // Listen for session state changes (for logging only, display mode is controlled by admin)
    newSocket.on('session:state-change', (data: { state: any; timestamp: number }) => {
      console.log('[DISPLAY] Session state change:', data.state);
      // Il display mode è controllato manualmente dall'admin via display:projection-update
      // Non aggiorniamo automaticamente il mode basandoci sullo stato della sessione
    });

    return () => {
      newSocket.disconnect();
    };
  }, [sessionId]);

  return {
    socket,
    mode,
    isConnected,
  };
}
